import React from 'react';
import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, query, where, getDocs, writeBatch, limit, doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { getToken } from 'firebase/messaging';
import { signOut, type User } from 'firebase/auth';
import { functions, db, messaging, auth } from '../lib/firebase';
import { Target, Mail, Send, Users, User as UserIcon, AlertTriangle, Trash2, Percent, Bell } from 'lucide-react';
import TwoFactorSetup from './TwoFactorSetup';

interface Member {
  id: string;
  email: string;
  role: string;
}

export default function Settings({ householdId, userId, user }: { householdId: string; userId: string; user: User }) {
  const [inviteEmail, setInviteEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [members, setMembers] = useState<Member[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(true);

  const [wipeSelection, setWipeSelection] = useState<string[]>([]);
  const [wiping, setWiping] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  const myRole = members.find(m => m.id === userId)?.role || 'member';

  const handleRemoveMember = async (member: Member) => {
    if (!window.confirm(`Remove ${member.email} from this household? They'll be moved to their own new, empty household.`)) return;
    setRemovingMemberId(member.id);
    try {
      const removeMember = httpsCallable(functions, 'removeMember');
      await removeMember({ memberUid: member.id });
      setMembers(prev => prev.filter(m => m.id !== member.id));
    } catch (err: any) {
      alert('Failed to remove member: ' + (err.message || err));
    } finally {
      setRemovingMemberId(null);
    }
  };
  
  const [marginalTaxRate, setMarginalTaxRate] = useState('');
  const [marginalTaxRateSpouse, setMarginalTaxRateSpouse] = useState('');
  const [savingTax, setSavingTax] = useState(false);
  const [taxSuccess, setTaxSuccess] = useState(false);

  const wipeOptions = [
    { id: 'transactions', label: 'Transactions' },
    { id: 'bank_accounts', label: 'Bank Accounts' },
    { id: 'debts', label: 'Debts (Credit Cards, Loans, Lines of Credit)' },
    { id: 'planned_payments', label: 'Planned Payments' },
    { id: 'recurring_expenses', label: 'Recurring Expenses' },
    { id: 'incomes', label: 'Incomes' },
    { id: 'goals', label: 'Goals' },
    { id: 'wishlist', label: 'Wishlist' },
    { id: 'ai_conversations', label: 'AI Strategy Conversations' },
    { id: 'monthly_reports', label: 'Monthly Reports' }
  ];

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const q = query(collection(db, 'users'), where('householdId', '==', householdId));
        const snapshot = await getDocs(q);
        const fetchedMembers = snapshot.docs.map(doc => ({
          id: doc.id,
          email: doc.data().email,
          role: doc.data().role || 'member'
        })) as Member[];
        setMembers(fetchedMembers);
        
        // Fetch household tax rate
        const hDoc = await getDoc(doc(db, 'households', householdId));
        if (hDoc.exists() && hDoc.data().marginalTaxRate) {
          setMarginalTaxRate(hDoc.data().marginalTaxRate.toString());
        }
        if (hDoc.exists() && hDoc.data().marginalTaxRateSpouse) {
          setMarginalTaxRateSpouse(hDoc.data().marginalTaxRateSpouse.toString());
        }
      } catch (err) {
        console.error("Failed to fetch household data:", err);
      } finally {
        setLoadingMembers(false);
      }
    };
    fetchMembers();
  }, [householdId]);

  const handleSaveTaxRate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!marginalTaxRate) return;
    setSavingTax(true);
    setTaxSuccess(false);
    try {
      await setDoc(doc(db, 'households', householdId), {
        marginalTaxRate: marginalTaxRate ? Number(marginalTaxRate) : 0,
        marginalTaxRateSpouse: marginalTaxRateSpouse ? Number(marginalTaxRateSpouse) : 0
      }, { merge: true });
      setTaxSuccess(true);
      setTimeout(() => setTaxSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save tax rate:', err);
      alert('Failed to save tax rate');
    } finally {
      setSavingTax(false);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail) return;
    
    setLoading(true);
    setError('');
    setSuccess(false);
    
    try {
      const inviteUserFn = httpsCallable<{ email: string }, { success: boolean }>(functions, 'inviteUser');
      await inviteUserFn({ email: inviteEmail });
      setSuccess(true);
      setInviteEmail('');
    } catch (err: any) {
      console.error('Failed to invite user:', err);
      setError(err.message || 'Failed to send invite');
    } finally {
      setLoading(false);
    }
  };

  const handleWipeData = async () => {
    if (wipeSelection.length === 0) {
      alert("Please select at least one data category to wipe.");
      return;
    }

    if (!window.confirm(`Are you absolutely sure you want to permanently delete the selected data (${wipeSelection.length} categories)?\n\nThis action is irreversible and cannot be undone.`)) {
      return;
    }

    setWiping(true);
    try {
      for (const collectionName of wipeSelection) {
        let isDone = false;
        while (!isDone) {
          const q = query(collection(db, collectionName), where('householdId', '==', householdId), limit(450));
          const snapshot = await getDocs(q);
          
          if (snapshot.empty) {
            isDone = true;
            break;
          }

          const batch = writeBatch(db);
          snapshot.docs.forEach((document) => {
            batch.delete(document.ref);
          });
          
          await batch.commit();
        }
      }
      alert('Selected data has been successfully wiped.');
      setWipeSelection([]);
    } catch (e: any) {
      console.error("Failed to wipe data:", e);
      alert("An error occurred while wiping data: " + e.message);
    } finally {
      setWiping(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    if (!window.confirm(
      members.length > 1
        ? 'This permanently deletes your own account and profile. The shared household data stays intact for the other member(s). This cannot be undone. Continue?'
        : 'This permanently deletes your account and ALL household data -- transactions, budgets, goals, and every linked bank connection (which will also be disconnected at Plaid). This cannot be undone. Continue?'
    )) return;

    setDeletingAccount(true);
    try {
      const deleteAccountFn = httpsCallable(functions, 'deleteAccount');
      await deleteAccountFn();
      await signOut(auth);
      // The account no longer exists -- send the browser back to a clean
      // sign-in screen rather than leaving it on a now-defunct session.
      window.location.href = '/';
    } catch (err: any) {
      console.error('Failed to delete account:', err);
      alert('Failed to delete account: ' + (err.message || err));
      setDeletingAccount(false);
    }
  };

  const handleEnableNotifications = async () => {
    if (!messaging) {
      alert("Push notifications are not supported in this browser.");
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        const token = await getToken(messaging, { 
          // vapidKey: "YOUR_VAPID_KEY_HERE" if required by FCM 
        });
        
        if (token) {
          // Save the token to the user document
          await updateDoc(doc(db, 'users', userId), {
            fcmToken: token
          });
          alert("Push notifications enabled successfully! You will now receive payment alerts.");
        } else {
          alert("Failed to get push token. Please try again.");
        }
      } else {
        alert("Notification permission denied. You can change this in your browser settings.");
      }
    } catch (err) {
      console.error("Failed to enable notifications:", err);
      alert("Error enabling notifications. (Make sure you are not in Incognito mode)");
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3 mb-8">
        <div className="p-3 bg-blue-100 text-blue-600 rounded-lg">
          <Target className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
          <p className="text-slate-500">Manage your household and preferences</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-slate-400" />
            Household Sharing
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Invite family members to share the same budget dashboard. When they sign up with the invited email, they will automatically join your household.
          </p>
        </div>
        
        <div className="p-6">
          <form onSubmit={handleInvite} className="max-w-md">
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Invite by Email
            </label>
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="spouse@example.com"
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading || !inviteEmail}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Sending...' : 'Invite'}
                <Send className="w-4 h-4" />
              </button>
            </div>
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
            {success && <p className="mt-2 text-sm text-green-600">Invite sent successfully! They can now sign up to join your household.</p>}
          </form>

          <div className="mt-8 pt-6 border-t border-slate-200">
            <h3 className="text-sm font-medium text-slate-900 mb-4">Household Details</h3>
            <div className="bg-slate-50 rounded-lg p-4 mb-4">
              <div className="flex justify-between items-center mb-4 pb-4 border-b border-slate-200">
                <span className="text-sm text-slate-500">Household ID</span>
                <span className="text-xs font-mono bg-slate-200 text-slate-700 px-2 py-1 rounded">{householdId}</span>
              </div>
              
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Members ({members.length})</h4>
                {loadingMembers ? (
                  <p className="text-sm text-slate-400">Loading members...</p>
                ) : members.length === 0 ? (
                  <p className="text-sm text-slate-400">No members found.</p>
                ) : (
                  <div className="space-y-2">
                    {members.map(member => (
                      <div key={member.id} className="flex items-center gap-3 bg-white p-3 rounded-md border border-slate-100">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                          <UserIcon className="w-4 h-4" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-900">{member.email}</p>
                          <p className="text-xs text-slate-500 capitalize">{member.role}</p>
                        </div>
                        {myRole === 'owner' && member.id !== userId && (
                          <button
                            onClick={() => handleRemoveMember(member)}
                            disabled={removingMemberId === member.id}
                            className="text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50 px-2 py-1"
                          >
                            {removingMemberId === member.id ? 'Removing…' : 'Remove'}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <h3 className="text-sm font-medium text-slate-900 mb-4 mt-6">Security</h3>
            <div className="mb-4">
              <TwoFactorSetup user={user} />
            </div>

            <h3 className="text-sm font-medium text-slate-900 mb-4 mt-6">Push Notifications</h3>
            <div className="bg-slate-50 rounded-lg p-4 mb-4 border border-slate-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">
                  <Bell className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-slate-900">Payment Due Alerts</h4>
                  <p className="text-xs text-slate-500">Get notified on this device when a payment is coming up.</p>
                </div>
              </div>
              <button
                onClick={handleEnableNotifications}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors w-full sm:w-auto mt-2"
              >
                Enable Notifications on this Device
              </button>
            </div>
            
            <h3 className="text-sm font-medium text-slate-900 mb-4 text-red-600">Advanced / Migration</h3>
            <div className="bg-red-50 rounded-lg p-4 border border-red-100">
              <p className="text-sm text-slate-700 mb-3">If you are missing data from before the household update, click this button to migrate your existing data to the new household format.</p>
              <button
                onClick={async () => {
                  try {
                    alert('Starting migration...');
                    const runMigration = httpsCallable(functions, 'runMigration');
                    await runMigration();
                    alert('Migration complete! Please refresh the page.');
                  } catch(e) {
                    alert('Migration failed: ' + e);
                  }
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700"
              >
                Migrate Old Data
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mt-6">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Percent className="w-5 h-5 text-slate-400" />
            Tax Settings (Smith Maneuver)
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Set your marginal tax rate to allow the AI to project tax refunds on your investment loans, which accelerate your mortgage payoff.
          </p>
        </div>
        
        <div className="p-6">
          <form onSubmit={handleSaveTaxRate} className="max-w-xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Primary Marginal Tax Rate (%)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.1"
                    value={marginalTaxRate}
                    onChange={(e) => setMarginalTaxRate(e.target.value)}
                    placeholder="e.g. 43.4"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:blue-500 focus:border-blue-500 outline-none transition-colors"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Spouse Marginal Tax Rate (%)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.1"
                    value={marginalTaxRateSpouse}
                    onChange={(e) => setMarginalTaxRateSpouse(e.target.value)}
                    placeholder="e.g. 31.5"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:blue-500 focus:border-blue-500 outline-none transition-colors"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={savingTax}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {savingTax ? 'Saving...' : 'Save Tax Rates'}
              </button>
            </div>
            {taxSuccess && <p className="mt-2 text-sm text-green-600">Tax rates saved successfully!</p>}
          </form>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-rose-200 overflow-hidden mt-6">
        <div className="p-6 border-b border-rose-100 bg-rose-50/50">
          <h2 className="text-lg font-bold text-rose-700 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Data Management (Factory Reset)
          </h2>
          <p className="text-sm text-rose-600/80 mt-1">
            Permanently delete your data to start fresh. This action cannot be undone.
          </p>
        </div>
        
        <div className="p-6">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Select data to wipe
          </label>
          <div className="mb-4 text-sm text-slate-500 flex justify-between items-center">
            <span>Hold Ctrl/Cmd to select multiple options.</span>
            <button 
              type="button" 
              onClick={() => setWipeSelection(wipeOptions.map(o => o.id))}
              className="text-blue-600 hover:text-blue-700 font-medium text-xs"
            >
              Select All
            </button>
          </div>
          
          <select 
            multiple
            value={wipeSelection}
            onChange={(e) => {
              const selectedOptions = Array.from(e.target.selectedOptions).map((option: HTMLOptionElement) => option.value);
              setWipeSelection(selectedOptions);
            }}
            className="w-full h-48 p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 outline-none text-sm mb-4"
          >
            {wipeOptions.map(option => (
              <option key={option.id} value={option.id} className="p-2 hover:bg-slate-100 rounded cursor-pointer">
                {option.label}
              </option>
            ))}
          </select>

          <button
            onClick={handleWipeData}
            disabled={wiping || wipeSelection.length === 0}
            className="flex items-center justify-center gap-2 w-full py-3 bg-rose-600 text-white rounded-lg font-medium hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {wiping ? (
              'Wiping Data...'
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                Wipe Selected Data
              </>
            )}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-rose-200 overflow-hidden mt-6">
        <div className="p-6 border-b border-rose-100 bg-rose-50/50">
          <h2 className="text-lg font-bold text-rose-700 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Delete My Account
          </h2>
          <p className="text-sm text-rose-600/80 mt-1">
            {members.length > 1
              ? "Permanently deletes your own login and profile. Shared household data stays with the household for the other member(s)."
              : "Permanently deletes your account and every piece of household data, including disconnecting any linked bank accounts at Plaid. This is irreversible."}
          </p>
        </div>
        <div className="p-6">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Type <span className="font-mono font-bold">DELETE</span> to confirm
          </label>
          <input
            type="text"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 outline-none mb-4"
            placeholder="DELETE"
          />
          <button
            onClick={handleDeleteAccount}
            disabled={deletingAccount || deleteConfirmText !== 'DELETE'}
            className="flex items-center justify-center gap-2 w-full py-3 bg-rose-600 text-white rounded-lg font-medium hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {deletingAccount ? (
              'Deleting Account...'
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                Delete My Account
              </>
            )}
          </button>
        </div>
      </div>

      <p className="text-center text-xs text-slate-400">
        <a
          href="https://wecrafttech.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-slate-600"
        >
          Privacy Policy
        </a>
      </p>
    </div>
  );
}
